import { type ButtonHTMLAttributes } from "react";
import { FOCUS_RING } from "./tokens";

// 어드민 버튼 — variant 로 위계를 잡는다.
//   primary   : 화면당 1개 (저장·완료 처리 등 주 액션)
//   secondary : 취소·조회처럼 되돌릴 수 있는 액션
//   ghost     : 표 안 인라인 액션 (수정·상세)
//   danger    : 완료 해제·체험 종료 등 파괴적 액션
//   dangerSolid : 환불·페이백처럼 돈이 나가는 팝업의 실행 버튼 — 서비스 테마의 경고색(theme.ts HighlightWithAlert)
// loading 이 true 면 자동으로 disabled 되고 loadingLabel 을 보여준다 (호출처에서 삼항 반복 금지).
type Variant = "primary" | "secondary" | "ghost" | "danger" | "dangerSolid";
type Size = "sm" | "md";

// disabled 는 배경·테두리로 비활성을 알리고 글자는 slate-500 이상을 유지한다.
// 흰 글자 on slate-300 같은 조합은 대비 1.7:1 로 읽히지 않는다 (본문 기준 AA 는 4.5:1).
const VARIANT: Record<Variant, string> = {
  primary:
    "bg-[#0F55A7] text-white hover:bg-[#0C4585] active:translate-y-px disabled:bg-slate-100 disabled:text-slate-500",
  secondary:
    "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 active:translate-y-px disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500",
  ghost:
    "text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:translate-y-px disabled:text-slate-500",
  danger:
    "border border-rose-300 bg-white text-rose-600 hover:bg-rose-50 active:translate-y-px disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500",
  dangerSolid:
    "bg-[#F62754] text-white hover:bg-[#F62754]/90 active:translate-y-px disabled:bg-slate-100 disabled:text-slate-500",
};

const SIZE: Record<Size, string> = {
  sm: "h-8 px-2.5 text-sm",
  md: "h-9 px-3.5 text-sm",
};

export default function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  loadingLabel,
  className = "",
  disabled,
  children,
  ...rest
}: {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  loadingLabel?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      // whitespace-nowrap: 라벨이 2줄로 접히면 버튼이 깨진다
      className={`inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors disabled:cursor-not-allowed ${FOCUS_RING} ${VARIANT[variant]} ${SIZE[size]} ${className}`}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && loadingLabel ? loadingLabel : children}
    </button>
  );
}
